import React, { useEffect, useState } from "react";
import { Plus, BookOpen, Trash2, Share2, ChevronRight, Send, Search, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AttachmentPreview from "../../components/AttachmentPreview";
import MultiUploader from "../../components/MultiUploader";
import Loading from "../../components/Loading";

function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return ""; } }

function ResourceComposer({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [attIds, setAttIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/community-resources", { title, body, attachment_ids: attIds });
      onCreated(r.data);
      setTitle(""); setBody(""); setAttIds([]);
      setOpen(false);
      toast.success("RESOURCE POSTED");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SUBMIT FAILED");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="sat-btn sat-btn-solid" data-testid="res-new-btn">
        <Plus size={12} /> SHARE RESOURCE
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="sat-brackets sat-surface border border-[color:var(--sat-cyan)] p-4 space-y-3" data-testid="res-composer">
      <div className="sat-label sat-cyan">NEW RESOURCE / THEORY</div>
      <input
        data-testid="res-title" value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="TITLE" maxLength={200} required minLength={4}
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm"
      />
      <textarea
        data-testid="res-body" value={body} onChange={(e) => setBody(e.target.value)}
        placeholder="Share your theory, notes, or a short description of the attached files…"
        rows={6} maxLength={8000} required minLength={2}
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm resize-none"
      />
      <MultiUploader ids={attIds} onChange={setAttIds} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setOpen(false)} className="sat-btn !py-2 !px-3">CANCEL</button>
        <button type="submit" disabled={busy || !title.trim() || !body.trim()} data-testid="res-submit"
          className="sat-btn sat-btn-solid !py-2 !px-4 disabled:opacity-40">
          {busy ? "TRANSMITTING…" : "POST"}
        </button>
      </div>
    </form>
  );
}

function ReplyComposer({ rid, onPosted }) {
  const [body, setBody] = useState("");
  const [attIds, setAttIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/community-resources/${rid}/replies`, { body, attachment_ids: attIds });
      onPosted(r.data);
      setBody(""); setAttIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "REPLY FAILED");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-2 border-t border-[color:var(--sat-border)] pt-3">
      <textarea
        data-testid={`res-reply-body-${rid}`}
        value={body} onChange={(e) => setBody(e.target.value)}
        placeholder="Reply — add your take, links, or files"
        rows={3} maxLength={4000}
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm resize-none"
      />
      <MultiUploader ids={attIds} onChange={setAttIds} />
      <div className="flex justify-end">
        <button type="submit" disabled={busy || !body.trim()} data-testid={`res-reply-submit-${rid}`}
          className="sat-btn sat-btn-solid !py-2 !px-4 disabled:opacity-40">
          <Send size={11} /> {busy ? "POSTING…" : "POST REPLY"}
        </button>
      </div>
    </form>
  );
}

function ResourceThread({ r, currentUser, isAdmin, onDelete }) {
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const rr = await api.get(`/community-resources/${r.id}/replies`);
      setReplies(rr.data.results || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && replies.length === 0) load();
  };
  const doDelete = async () => {
    if (!window.confirm("Delete this resource and all replies?")) return;
    try {
      await api.delete(`/community-resources/${r.id}`);
      onDelete(r.id);
      toast.success("RESOURCE DELETED");
    } catch (err) { toast.error(err?.response?.data?.detail || "DELETE FAILED"); }
  };
  const doShare = async () => {
    const url = `${window.location.origin}/community#resources/${r.id}`;
    try {
      if (navigator.share) await navigator.share({ title: r.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("LINK COPIED"); }
    } catch { /* cancelled */ }
  };
  const deleteReply = async (rep) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/community-resources/${r.id}/replies/${rep.id}`);
      setReplies(cur => cur.filter(x => x.id !== rep.id));
    } catch (err) { toast.error(err?.response?.data?.detail || "DELETE FAILED"); }
  };
  const canDeleteReply = (rep) => currentUser && (rep.author_id === currentUser.id || isAdmin);
  const canDeleteResource = currentUser && (r.author_id === currentUser.id || isAdmin);

  return (
    <article className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-4" data-testid={`res-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <button onClick={toggle} className="flex-1 text-left" data-testid={`res-toggle-${r.id}`}>
          <div className="sat-label sat-cyan flex items-center gap-2"><BookOpen size={12} /> RESOURCE · @{r.author_callsign}</div>
          <div className="font-display font-bold text-lg mt-1 flex items-center gap-2">
            <span className="flex-1">{r.title}</span>
            <ChevronRight size={16} className={`shrink-0 transition-transform ${open ? "rotate-90 sat-cyan" : ""}`} />
          </div>
          <div className="sat-label mt-1 text-[color:var(--sat-text-dim)]">
            {fmtTime(r.created_at)} · {r.reply_count} {r.reply_count === 1 ? "REPLY" : "REPLIES"}
          </div>
        </button>
        <div className="flex gap-1 shrink-0">
          <button onClick={doShare} className="sat-btn !py-1 !px-2" title="Share" data-testid={`res-share-${r.id}`}>
            <Share2 size={11} />
          </button>
          {canDeleteResource && (
            <button onClick={doDelete} className="sat-btn sat-btn-danger !py-1 !px-2"
              data-testid={`res-delete-${r.id}`} title="Delete">
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <div className="text-sm whitespace-pre-wrap leading-relaxed">{r.body}</div>
          {r.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {r.attachments.map(a => <AttachmentPreview key={a.id} attachment={a} compact />)}
            </div>
          )}

          <div className="sat-label sat-cyan pt-2 border-t border-[color:var(--sat-border)]">
            THREAD · {replies.length || r.reply_count}
          </div>
          {loading && <div className="sat-label text-[color:var(--sat-text-dim)]">LOADING…</div>}
          {!loading && replies.length === 0 && (
            <div className="sat-label text-[color:var(--sat-text-dim)]" data-testid={`res-thread-empty-${r.id}`}>
              NO REPLIES YET.
            </div>
          )}
          <ul className="space-y-3" data-testid={`res-thread-${r.id}`}>
            {replies.map(rep => (
              <li key={rep.id} className="border-l-2 border-[color:var(--sat-border)] pl-3 py-2" data-testid={`res-reply-${rep.id}`}>
                <div className="sat-label sat-cyan flex items-center gap-2">
                  @{rep.author_callsign} · {fmtTime(rep.created_at)}
                  {canDeleteReply(rep) && (
                    <button onClick={() => deleteReply(rep)} className="ml-2 hover:text-red-400"
                      data-testid={`res-reply-delete-${rep.id}`} title="Delete reply">
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap mt-1">{rep.body}</div>
                {rep.attachments?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {rep.attachments.map(x => <AttachmentPreview key={x.id} attachment={x} compact />)}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {currentUser ? (
            <ReplyComposer rid={r.id} onPosted={(rep) => setReplies(cur => [...cur, rep])} />
          ) : (
            <div className="sat-label text-[color:var(--sat-text-dim)]">LOG IN TO REPLY.</div>
          )}
        </div>
      )}
    </article>
  );
}

export default function ResourcesTab() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");

  const load = async (query = "") => {
    setLoading(true);
    try {
      const path = query ? `/community-resources?q=${encodeURIComponent(query)}` : "/community-resources";
      const r = await api.get(path);
      setRows(r.data.results || []);
      setApplied(query);
    } catch (err) { toast.error(err?.response?.data?.detail || "LOAD FAILED"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(""); }, []);

  // Debounced auto-search on every keystroke (250ms).
  useEffect(() => {
    const t = setTimeout(() => {
      if (q !== applied) load(q.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div data-tab-key="resources" data-tab-content="1" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-md border border-[color:var(--sat-border)] focus-within:border-[color:var(--sat-cyan)] px-3 py-2"
             data-testid="res-search-wrap">
          <Search size={13} className="sat-cyan" />
          <input
            data-testid="res-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH TITLES + THEORIES"
            className="flex-1 bg-transparent outline-none font-mono text-sm placeholder:text-[color:var(--sat-text-dim)]"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} data-testid="res-search-clear"
              className="sat-cyan hover:opacity-80" title="Clear">
              <XIcon size={12} />
            </button>
          )}
        </div>
        {user && <ResourceComposer onCreated={(res) => setRows(cur => [res, ...cur])} />}
      </div>

      {applied && !loading && (
        <div className="sat-label text-[color:var(--sat-text-dim)]" data-testid="res-search-summary">
          {rows.length} RESULT{rows.length === 1 ? "" : "S"} FOR "{applied.toUpperCase()}"
        </div>
      )}

      {loading && <Loading message="LOADING RESOURCES" />}
      {!loading && rows.length === 0 && (
        <div className="sat-label text-center py-10" data-testid="res-empty">
          {applied
            ? `NO RESULTS FOR "${applied.toUpperCase()}"`
            : (user ? "NO RESOURCES YET — SHARE THE FIRST ONE." : "NO RESOURCES YET — LOG IN TO SHARE.")}
        </div>
      )}
      <div className="space-y-3">
        {rows.map(r => (
          <ResourceThread
            key={r.id} r={r} currentUser={user} isAdmin={isAdmin}
            onDelete={(id) => setRows(cur => cur.filter(x => x.id !== id))}
          />
        ))}
      </div>
    </div>
  );
}
